import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { toast } from 'react-hot-toast';
import * as api from './endpoints';

export const useActivities = (options?: { enabled?: boolean }) => {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;

  const { data: activities, isLoading } = useQuery({
    queryKey: ['activities'],
    queryFn: api.activities.list,
    enabled,
  });

  const metrics = useQuery({
    queryKey: ['activities', 'metrics'],
    queryFn: api.activities.metrics,
    enabled,
  });

  const createMutation = useMutation({
    mutationFn: api.activities.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      toast.success('Activity created successfully');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormData }) => api.activities.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      toast.success('Activity updated successfully');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.activities.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      toast.success('Activity deleted successfully');
    },
  });

  return {
    activities,
    metrics,
    isLoading,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    delete: deleteMutation.mutateAsync,
  };
};

export const useDonations = (options?: { enabled?: boolean }) => {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;

  const { data: donations, isLoading } = useQuery({
    queryKey: ['donations'],
    queryFn: api.donations.list,
    enabled,
  });

  const metrics = useQuery({
    queryKey: ['donations', 'metrics'],
    queryFn: api.donations.metrics,
    enabled,
  });

  const createMutation = useMutation({
    mutationFn: api.donations.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donations'] });
      toast.success('Donation created successfully');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormData }) => api.donations.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donations'] });
      toast.success('Donation updated successfully');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.donations.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['donations'] });
      toast.success('Donation deleted successfully');
    },
  });

  return {
    donations,
    metrics,
    isLoading,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    delete: deleteMutation.mutateAsync,
  };
};

export const useEventRSVPs = (eventId: string | null) => {
  return useQuery({
    queryKey: ['events', eventId, 'rsvps'],
    queryFn: () => api.events.getRSVPs(eventId!),
    enabled: !!eventId,
    staleTime: 30 * 1000,
  });
};

export const useEvents = (options?: { enabled?: boolean }) => {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;

  // Main events query
  const { data: events, isLoading } = useQuery({
    queryKey: ['events'],
    queryFn: api.events.list,
    enabled,
  });

  const getRSVPs = async (eventId: string) => {
    return api.events.getRSVPs(eventId);
  };

  const mutations = {
    create: useMutation({
      mutationFn: api.events.create,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['events'] });
        toast.success('Event created successfully');
      },
    }),

    update: useMutation({
      mutationFn: ({ id, data }: { id: string; data: FormData }) =>
        api.events.update(id, data),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['events'] });
        toast.success('Event updated successfully');
      },
    }),

    delete: useMutation({
      mutationFn: api.events.delete,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['events'] });
        toast.success('Event deleted successfully');
      },
    }),

    createRsvp: useMutation({
      mutationFn: ({ eventId, data }: { eventId: string; data: FormData }) =>
        api.events.submitRsvp(eventId, data),
      onSuccess: (_, { eventId }) => {
        queryClient.invalidateQueries({ queryKey: ['events', eventId, 'rsvps'] });
        queryClient.invalidateQueries({ queryKey: ['events'] });
        toast.success('Attendee added');
      },
    }),

    updateRsvp: useMutation({
      mutationFn: ({ eventId, rsvpId, data }: { eventId: string; rsvpId: string; data: FormData }) =>
        api.events.updateRsvp(eventId, rsvpId, data),
      onSuccess: (_, { eventId }) => {
        queryClient.invalidateQueries({ queryKey: ['events', eventId, 'rsvps'] });
        queryClient.invalidateQueries({ queryKey: ['events'] });
        toast.success('Attendee updated');
      },
    }),

    deleteRsvp: useMutation({
      mutationFn: ({ eventId, rsvpId }: { eventId: string; rsvpId: string }) =>
        api.events.deleteRsvp(eventId, rsvpId),
      onSuccess: (_, { eventId }) => {
        queryClient.invalidateQueries({ queryKey: ['events', eventId, 'rsvps'] });
        queryClient.invalidateQueries({ queryKey: ['events'] });
        toast.success('Attendee removed');
      },
    }),
  };

  return {
    events,
    isLoading,
    getRSVPs,
    create: mutations.create.mutateAsync,
    update: mutations.update.mutateAsync,
    delete: mutations.delete.mutateAsync,
    createRsvp: mutations.createRsvp.mutateAsync,
    updateRsvp: mutations.updateRsvp.mutateAsync,
    deleteRsvp: mutations.deleteRsvp.mutateAsync,
  };
};

export const usePartners = () => {
  const queryClient = useQueryClient();

  const { data: partners, isLoading } = useQuery({
    queryKey: ['partners'],
    queryFn: api.partners.list,
  });

  const createMutation = useMutation({
    mutationFn: api.partners.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success('Partner created successfully');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormData }) => api.partners.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success('Partner updated successfully');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.partners.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['partners'] });
      toast.success('Partner deleted successfully');
    },
  });

  return {
    partners,
    isLoading,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    delete: deleteMutation.mutateAsync,
  };
};

export const useContacts = () => {
  const queryClient = useQueryClient();

  const { data: contacts, isLoading } = useQuery({
    queryKey: ['contacts'],
    queryFn: api.contacts.list,
  });

  const createMutation = useMutation({
    mutationFn: api.contacts.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast.success('Contact created successfully');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.contacts.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      toast.success('Contact deleted successfully');
    },
  });

  return {
    contacts,
    isLoading,
    create: createMutation.mutateAsync,
    delete: deleteMutation.mutateAsync,
  };
};

export const usePosters = () => {
  const queryClient = useQueryClient();

  const { data: posters, isLoading } = useQuery({
    queryKey: ['posters'],
    queryFn: api.posters.list,
  });

  const createMutation = useMutation({
    mutationFn: api.posters.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posters'] });
      toast.success('Poster created successfully');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: FormData }) => api.posters.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posters'] });
      toast.success('Poster updated successfully');
    },
  });

  const toggleMutation = useMutation({
    mutationFn: api.posters.toggle,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posters'] });
      toast.success('Poster status updated successfully');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.posters.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['posters'] });
      toast.success('Poster deleted successfully');
    },
  });

  return {
    posters,
    isLoading,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    toggle: toggleMutation.mutateAsync,
    delete: deleteMutation.mutateAsync,
  };
};

export const useProducts = (options?: { enabled?: boolean }) => {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;

  const { data: products, isLoading } = useQuery({
    queryKey: ['products'],
    queryFn: api.products.list,
    enabled,
  });

  const createMutation = useMutation({
    mutationFn: api.products.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product created successfully');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => api.products.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product updated successfully');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.products.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      toast.success('Product deleted successfully');
    },
  });

  return {
    products,
    isLoading,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    delete: deleteMutation.mutateAsync,
  };
};

export const useOrders = (options?: { enabled?: boolean }) => {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;

  const { data: orders, isLoading } = useQuery({
    queryKey: ['orders'],
    // Wrapped: passing the function directly sent react-query's context as request params
    queryFn: () => api.orders.list({ limit: 500 }),
    enabled,
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ id, status, trackingNumber, refundConfirmed }: { id: string; status: string; trackingNumber?: string; refundConfirmed?: boolean }) =>
      api.orders.updateStatus(id, status, { trackingNumber, refundConfirmed }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast.success('Order status updated successfully');
    },
  });

  return {
    orders,
    isLoading,
    updateStatus: updateStatusMutation.mutateAsync,
  };
};

export const useUploadMedia = () => {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('image', file);

      // We can optionally pass 'folder' here if needed, defaults to 'media' in backend
      return api.media.uploadImage(formData);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to upload image');
    },
  });
};

// RBAC Hooks
export const useRoles = (options?: { enabled?: boolean }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;

  const { data: roles, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: api.roles.list,
    enabled: enabled && isLoaded && isSignedIn,
  });

  const createMutation = useMutation({
    mutationFn: api.roles.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Role created successfully');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name: string; description: string } }) => api.roles.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Role updated successfully');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: api.roles.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Role deleted successfully');
    },
  });

  const updatePermissionsMutation = useMutation({
    mutationFn: ({ id, permissionIds }: { id: string; permissionIds: string[] }) => api.roles.updatePermissions(id, permissionIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      toast.success('Permissions updated successfully');
    },
  });

  return {
    roles,
    isLoading,
    create: createMutation.mutateAsync,
    update: updateMutation.mutateAsync,
    delete: deleteMutation.mutateAsync,
    updatePermissions: updatePermissionsMutation.mutateAsync,
  };
};

export const useRole = (id: string, options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;

  return useQuery({
    queryKey: ['roles', id],
    queryFn: () => api.roles.get(id),
    enabled: enabled && !!id,
  });
};

export const usePermissions = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;

  return useQuery({
    queryKey: ['permissions'],
    queryFn: api.permissions.list,
    enabled,
  });
};

export const useUsers = (params?: { query?: string; offset?: number; limit?: number }, options?: { enabled?: boolean }) => {
  const { isSignedIn, isLoaded } = useAuth();
  const enabled = options?.enabled ?? true;

  return useQuery({
    queryKey: ['users', params],
    queryFn: () => api.users.list(params),
    enabled: enabled && isLoaded && isSignedIn,
  });
};

export const useUserRoles = (userId: string, options?: { enabled?: boolean }) => {
  const queryClient = useQueryClient();
  const enabled = options?.enabled ?? true;

  const { data: roles, isLoading } = useQuery({
    queryKey: ['users', userId, 'roles'],
    queryFn: () => api.users.getRoles(userId),
    enabled: enabled && !!userId,
  });

  const assignRoleMutation = useMutation({
    mutationFn: (roleId: string) => api.users.assignRole(userId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', userId, 'roles'] });
      toast.success('Role assigned successfully');
    },
  });

  const removeRoleMutation = useMutation({
    mutationFn: (roleId: string) => api.users.removeRole(userId, roleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users', userId, 'roles'] });
      toast.success('Role removed successfully');
    },
  });

  return {
    roles,
    isLoading,
    assignRole: assignRoleMutation.mutateAsync,
    removeRole: removeRoleMutation.mutateAsync,
    isAssigning: assignRoleMutation.isPending,
  };
};

export const useAuditLogs = (params?: { userId?: string; action?: string; resourceType?: string; startDate?: string; endDate?: string; limit?: number; offset?: number }, options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;

  return useQuery({
    queryKey: ['audit-logs', params],
    queryFn: () => api.auditLogs.list(params),
    enabled,
  });
};